import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, signal } from '@angular/core';
import { debounceTime } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { Course } from '@mira-avant/mtlibs';
import { CourseService } from '../../../../services/course.service';
import { AngularModule } from '../../../../modules/angular.module';
import { NzTableModule, NzTableSortFn, NzTableSortOrder } from 'ng-zorro-antd/table';
import { NzEmptyModule } from 'ng-zorro-antd/empty';

@Component({
	selector: 'app-course-selector',
	standalone: true,
	imports: [AngularModule, NzTableModule, NzEmptyModule],
	templateUrl: './course-selector.component.html',
	styleUrl: './course-selector.component.scss',
})
export class CourseSelectorComponent implements OnChanges {
	@Input('clientUuids') public clientUuids: string[] | undefined;
	@Output('onSelectCourse') onSelectCourseEmitter = new EventEmitter<Course | undefined>();

	public courses = signal<Course[]>([]);
	public selectedCourse = signal<Course | undefined>(undefined);
	public sortOrder = signal<NzTableSortOrder>(null);

	private changes$ = new Subject<void>();
	private sub?: Subscription;

	constructor(public courseService: CourseService) {
		this.sub = this.changes$.pipe(debounceTime(1000)).subscribe(() => {
			this.loadCourses();
		});
	}

	async ngOnChanges(changes: SimpleChanges): Promise<void> {
		if (changes['clientUuids']) {
			this.changes$.next();
		}
	}

	async loadCourses(): Promise<void> {
		if (!this.clientUuids || this.clientUuids.length === 0) {
			this.courses.set([]);
			this.selectedCourse.set(undefined);
			this.onSelectCourseEmitter.emit(undefined);
			return;
		}

		this.courses.set([]);
		this.selectedCourse.set(undefined);

		let allCourses: Course[] = [];

		for (const clientUuid of this.clientUuids) {
			const courses = await this.courseService.getByClient$(clientUuid);
			allCourses = allCourses.concat(courses);
		}

		this.courses.set(allCourses);
	}

	public selectCourse(course: Course | undefined): void {
		const current = this.selectedCourse();
		if (current?.uuid === course?.uuid) {
			this.selectedCourse.set(undefined);
		} else {
			this.selectedCourse.set(course);
		}
		this.onSelectCourseEmitter.emit(this.selectedCourse());
	}

	public sortNameFn: NzTableSortFn<Course> = (a, b) => {
		const nameA = a.active_text?.name?.toLowerCase() || '';
		const nameB = b.active_text?.name?.toLowerCase() || '';
		return nameA.localeCompare(nameB);
	};

	public onSortOrderChange(order: NzTableSortOrder): void {
		this.sortOrder.set(order);
	}

	ngOnDestroy(): void {
		if (this.sub) {
			this.sub.unsubscribe();
		}
	}
}
